import React, { useState, useRef, useEffect } from 'react';
import { Upload, AlertTriangle, CheckCircle, FileText, Loader } from 'lucide-react';
import Button from '../../../ui-kit/Button';
import Card from '../../../ui-kit/Card';
import { useCustomers } from '../hooks/useCustomers';
import { saveChunk, clearDB, getAllRecords, countRecords } from '../../../core/storage/indexedDB';
import { CustomerService } from '../services/CustomerService';

import { auth } from '../../../integrations/firebase';

const UploadCustomers = () => {
    const { dispatch } = useCustomers();
    const [file, setFile] = useState(null);
    const [status, setStatus] = useState('idle'); // idle, parsing, aggregating, uploading, complete, error
    const [progress, setProgress] = useState(0);
    const [stats, setStats] = useState({ parsed: 0, aggregated: 0 });
    const [error, setError] = useState('');

    // Debug Auth
    const user = auth.currentUser;

    const parseWorkerRef = useRef(null);
    const aggregateWorkerRef = useRef(null);

    useEffect(() => {
        // Initialize Workers
        parseWorkerRef.current = new Worker(new URL('../../../workers/customerUploadWorker.js', import.meta.url), { type: 'module' });
        aggregateWorkerRef.current = new Worker(new URL('../../../workers/aggregatorWorker.js', import.meta.url), { type: 'module' });

        return () => {
            parseWorkerRef.current?.terminate();
            aggregateWorkerRef.current?.terminate();
        };
    }, []);

    const handleFileChange = (e) => {
        if (e.target.files[0]) {
            setFile(e.target.files[0]);
            setError('');
            setStatus('idle');
        }
    };

    const startProcess = async () => {
        if (!file) return;

        setStatus('parsing');
        setProgress(0);
        await clearDB(); // Clear temp storage

        // 1. Parsing Phase
        parseWorkerRef.current.postMessage({ file });

        parseWorkerRef.current.onmessage = async (e) => {
            const { type, data, error } = e.data;

            if (type === 'CHUNK_PROCESSED') {
                await saveChunk(data);
                setStats(prev => ({ ...prev, parsed: prev.parsed + data.length }));
            } else if (type === 'COMPLETE') {
                startAggregation();
            } else if (type === 'ERROR') {
                setError(error);
                setStatus('error');
            }
        };
    };

    const startAggregation = async () => {
        setStatus('aggregating');
        const totalRecords = await countRecords();
        const rawRecords = await getAllRecords(); // In real 300k scenario, we might need to stream this too

        aggregateWorkerRef.current.postMessage({ rawRecords });

        aggregateWorkerRef.current.onmessage = async (e) => {
            const { type, data, error } = e.data;

            if (type === 'AGGREGATION_COMPLETE') {
                setStats(prev => ({ ...prev, aggregated: data.length }));
                startUpload(data);
            } else if (type === 'ERROR') {
                setError(error);
                setStatus('error');
            }
        };
    };

    const startUpload = async (aggregatedData) => {
        setStatus('uploading');
        try {
            await CustomerService.uploadToFirebaseInBatches(aggregatedData, (percent) => {
                setProgress(percent);
            });
            setStatus('complete');
            await clearDB(); // Cleanup
            dispatch({ type: 'SET_CUSTOMERS', payload: aggregatedData }); // Update context
        } catch (err) {
            setError(err.message);
            setStatus('error');
        }
    };

    const handleDownloadSample = () => {
        const headers = [
            "DATE", "ORDER NUMBER", "CUSTOMER NAME", "CUSTOMER MOBILE", "CUSTOMER MOBILE 2", 
            "ADDRESS", "PAYMENT MODE", "PINCODE", "CITY", "STATE", "PRODUCT CODE", "PRODUCT NAME", "PRICE", 
            "COURIER COMPANY", "AWB NUMBER", "DELIVERY STATUS", "REMARK FOR THE CUSTOMER", 
            "COMPLAINT MONTH 01-2025", "COMPLAINT NO", "COMPLAINT RESOLVED OR NOT", "COMPLAINT DETAILS"
        ];
        const sampleRow = [
            "2024-03-01", "DO000123", "Amit Kumar", "9876543210", "", 
            "Flat 101, Main Road", "Prepaid", "560001", "Bengaluru", "Karnataka", "SKU123", "Organic Honey", "999", 
            "Delhivery", "1234567890", "Delivered", "Called but no response, delivered next day", 
            "", "", "", ""
        ];
        const csvContent = "data:text/csv;charset=utf-8," + headers.join(",") + "\n" + sampleRow.map(v => `"${v}"`).join(",");
        const encodedUri = encodeURI(csvContent);
        const link = document.createElement("a");
        link.setAttribute("href", encodedUri);
        link.setAttribute("download", "sample_upload_format.csv");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return (
        <div className="space-y-6">
            <Card className="p-8">
                <div className="flex flex-col items-center justify-center border-2 border-dashed border-slate-300 rounded-xl p-10 bg-slate-50 hover:bg-slate-100 transition-colors">
                    <Upload size={48} className="text-slate-400 mb-4" />
                    <h3 className="text-xl font-bold text-slate-700 mb-2">Upload Customer CSV</h3>
                    <p className="text-slate-500 mb-6 text-center max-w-md">
                        Supports large files (300k+ rows). Strict format required.
                    </p>

                    <input
                        type="file"
                        accept=".csv"
                        onChange={handleFileChange}
                        className="hidden"
                        id="csv-upload"
                        disabled={status !== 'idle' && status !== 'error' && status !== 'complete'}
                    />
                    <div className="flex items-center gap-4 mt-6">
                        <label
                            htmlFor="csv-upload"
                            className="bg-purple-600 text-white px-6 py-3 rounded-lg font-bold cursor-pointer hover:bg-purple-700 transition-colors shadow-sm"
                        >
                            {file ? 'Change File' : 'Select CSV File'}
                        </label>
                        <button
                            onClick={handleDownloadSample}
                            className="bg-slate-100 text-slate-700 border border-slate-200 px-6 py-3 rounded-lg font-bold hover:bg-slate-200 transition-colors flex items-center gap-2 shadow-sm"
                        >
                            <FileText size={18} />
                            Sample CSV
                        </button>
                    </div>

                    {file && <p className="mt-4 text-sm font-medium text-slate-600">{file.name}</p>}

                    {/* Auth Debug */}
                    <div className="mt-4 text-xs text-center text-slate-400">
                        {user ? `Logged in as: ${user.email}` : '⚠️ NOT LOGGED IN - Upload will fail'}
                    </div>
                </div>

                {/* Progress Section */}
                {(status !== 'idle') && (
                    <div className="mt-8 space-y-4">
                        <div className="flex items-center justify-between text-sm font-bold text-slate-700">
                            <span className="capitalize flex items-center gap-2">
                                {status === 'parsing' && <Loader className="animate-spin" size={16} />}
                                {status === 'aggregating' && <Loader className="animate-spin" size={16} />}
                                {status === 'uploading' && <Loader className="animate-spin" size={16} />}
                                {status === 'complete' && <CheckCircle className="text-green-500" size={16} />}
                                {status === 'error' && <AlertTriangle className="text-red-500" size={16} />}
                                {status}...
                            </span>
                            <span>{status === 'uploading' ? `${progress}%` : ''}</span>
                        </div>

                        {/* Progress Bar */}
                        <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                            <div
                                className={`h-full transition-all duration-500 ${status === 'error' ? 'bg-red-500' : 'bg-purple-600'}`}
                                style={{ width: status === 'uploading' ? `${progress}%` : status === 'complete' ? '100%' : '100%' }}
                            />
                        </div>

                        {/* Stats */}
                        <div className="grid grid-cols-2 gap-4 mt-4">
                            <div className="bg-slate-50 p-3 rounded-lg border border-slate-200">
                                <p className="text-xs text-slate-500 uppercase font-bold">Rows Parsed</p>
                                <p className="text-lg font-bold text-slate-800">{stats.parsed.toLocaleString()}</p>
                            </div>
                            <div className="bg-slate-50 p-3 rounded-lg border border-slate-200">
                                <p className="text-xs text-slate-500 uppercase font-bold">Profiles Created</p>
                                <p className="text-lg font-bold text-slate-800">{stats.aggregated.toLocaleString()}</p>
                            </div>
                        </div>

                        {error && (
                            <div className="p-4 bg-red-50 text-red-600 rounded-lg text-sm font-medium flex items-center gap-2">
                                <AlertTriangle size={16} />
                                {error}
                            </div>
                        )}
                    </div>
                )}

                <div className="mt-6 flex justify-end">
                    <Button
                        onClick={startProcess}
                        disabled={!file || (status !== 'idle' && status !== 'error')}
                        className="w-full md:w-auto"
                    >
                        Start Processing
                    </Button>
                </div>
            </Card>
        </div>
    );
};

export default UploadCustomers;
